import { BaseQueryFn, FetchArgs, FetchBaseQueryError, createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react'
import { RootState } from '../store'

export interface responseArgs {
  status_code: number
  message: string
  data: any
  response?: any
}

export interface APIBaseResponse {
  status_code: number;
  message: string;
}
export interface deafultResponseAPIResponse extends APIBaseResponse {
  data: { [key: string]: any };

}
export interface deafultResponseDatArrayAPIResponse extends APIBaseResponse {
  data: Array<any>;

}
let baseUrl = ''; // Default value or empty

export const setApiConfig = ({ baseUrl: newBaseUrl }: { baseUrl: string }) => {
  baseUrl = newBaseUrl;
};

export function getApiBaseUrl(): string {
  const resolved = baseUrl || (import.meta as any).env?.CTY_API_URL || '';
  return String(resolved).replace(/\/$/, '');
}

/** Host for voice agent / vagent APIs; set at runtime via `setVoiceAgentConfig` (recommended) or from `CTY_VOICE_AGENT_BASE_URL` when the library is built with that env. */
function readCtyVoiceAgentBaseUrl(): string {
  const fromMeta = (import.meta as any).env?.CTY_VOICE_AGENT_BASE_URL;
  if (fromMeta) return String(fromMeta).replace(/\/$/, '');
  return '';
}

let voiceAgentBaseUrl = readCtyVoiceAgentBaseUrl();

/** TURN/STUN (Stunner) for browser WebRTC; set from env at module load or via `setVoiceAgentConfig`. */
export type WebRtcStunnerConfig = {
  host: string;
  username: string;
  credential: string;
};

function readCtyWebRtcStunnerFromEnv(): WebRtcStunnerConfig | null {
  const host = String((import.meta as any).env?.CTY_WEBRTC_STUNNER_HOST ?? '').trim();
  const username = String((import.meta as any).env?.CTY_WEBRTC_STUNNER_USER ?? '').trim();
  const credential = String((import.meta as any).env?.CTY_WEBRTC_STUNNER_CREDENTIAL ?? '').trim();
  if (!host || !username || !credential) return null;
  return { host, username, credential };
}

let webRtcStunnerConfig: WebRtcStunnerConfig | null = readCtyWebRtcStunnerFromEnv();

function readCtyMcpBaseUrl(): string {
  const fromMeta = (import.meta as any).env?.CTY_MCP_API_URL;
  if (fromMeta) return String(fromMeta).replace(/\/$/, '');
  return 'https://dev.mcp.circuitry.ai';
}

let mcpBaseUrl = readCtyMcpBaseUrl();

/**
 * Root URL for MCP capability APIs (e.g. `https://dev.mcp.circuitry.ai`), no trailing slash.
 */
export function getMcpBaseUrl(): string {
  return mcpBaseUrl;
}

export const setMcpConfig = ({ baseUrl: url }: { baseUrl: string }) => {
  const trimmed = String(url ?? '').trim();
  mcpBaseUrl = trimmed.replace(/\/$/, '');
};

/**
 * Stunner TURN/STUN credentials for voice WebRTC (hostname only, no `stun:` prefix).
 * Populated from `CTY_WEBRTC_*` at build time or `setVoiceAgentConfig({ webrtcStunner })` at runtime.
 */
export function getWebRtcStunnerConfig(): WebRtcStunnerConfig | null {
  return webRtcStunnerConfig;
}

/**
 * Root URL for vagent (e.g. `https://dev.vagent.circuitry.ai`), no trailing slash.
 * Prefer calling `setVoiceAgentConfig` from the host app so doc-data works when consumed from npm (Pal, etc.).
 */
export function getVoiceAgentBaseUrl(): string {
  return voiceAgentBaseUrl;
}

export type SetVoiceAgentConfigInput = {
  baseUrl: string;
  /** When all three fields are non-empty after trim, replaces Stunner config for `getWebRtcStunnerConfig`. */
  webrtcStunner?: WebRtcStunnerConfig;
};

/**
 * Set the voice-agent / vagent API host and optional WebRTC Stunner (TURN) settings. Call once at app
 * startup from `main.tsx`, typically with `import.meta.env.CTY_VOICE_AGENT_BASE_URL` (Vite). Omit
 * `webrtcStunner` to keep values from `CTY_WEBRTC_STUNNER_*` read at module load.
 */
export const setVoiceAgentConfig = ({ baseUrl: url, webrtcStunner }: SetVoiceAgentConfigInput) => {
  const t = String(url ?? '').trim();
  voiceAgentBaseUrl = t.replace(/\/$/, '');
  if (webrtcStunner) {
    const host = String(webrtcStunner.host ?? '').trim();
    const username = String(webrtcStunner.username ?? '').trim();
    const credential = String(webrtcStunner.credential ?? '').trim();
    if (host && username && credential) {
      webRtcStunnerConfig = { host, username, credential };
    }
  }
};

/** Host for agents stream APIs; set at runtime via `setAgentsStreamConfig` or from `CTY_AGENTS_STREAM_URL` at build. */
function readCtyAgentsStreamBaseUrl(): string {
  const fromMeta = (import.meta as any).env?.CTY_AGENTS_STREAM_URL;
  if (fromMeta) return String(fromMeta).replace(/\/$/, '');
  return '';
}

let agentsStreamBaseUrl = readCtyAgentsStreamBaseUrl();

/**
 * Root URL for agents stream (e.g. `https://agents.circuitry.ai`), no trailing slash.
 * Prefer calling `setAgentsStreamConfig` from the host app so doc-data/doc-ui work when consumed from npm (Pal, etc.).
 */
export function getAgentsStreamBaseUrl(): string {
  return agentsStreamBaseUrl;
}

/**
 * Set the agents-stream API host. Call once at app startup from `main.tsx` with
 * `import.meta.env.CTY_AGENTS_STREAM_URL` when consuming `@circuitry-ai/doc-ui` from npm.
 */
export const setAgentsStreamConfig = ({ baseUrl: url }: { baseUrl: string }) => {
  const t = String(url ?? '').trim();
  agentsStreamBaseUrl = t.replace(/\/$/, '');
};

/** Host for agentic analyst APIs; set at runtime via `setAgenticAnalystConfig` or from `CTY_AGENTIC_ANALYST_URL` at build. */
function readCtyAgenticAnalystBaseUrl(): string {
  const fromMeta = (import.meta as any).env?.CTY_AGENTIC_ANALYST_URL;
  if (fromMeta) return String(fromMeta).replace(/\/$/, '');
  return '';
}

let agenticAnalystBaseUrl = readCtyAgenticAnalystBaseUrl();

export function getAgenticAnalystBaseUrl(): string {
  return agenticAnalystBaseUrl;
}

export const setAgenticAnalystConfig = ({ baseUrl: url }: { baseUrl: string }) => {
  const t = String(url ?? '').trim();
  agenticAnalystBaseUrl = t.replace(/\/$/, '');
};

/** Host for the Web Agent runner (the server-side service that drives Playwright MCP); set via `setWebAgentConfig` or `CTY_WEB_AGENT_API_URL`. Defaults to the local dev runner. */
function readCtyWebAgentBaseUrl(): string {
  const fromMeta = (import.meta as any).env?.CTY_WEB_AGENT_API_URL;
  if (fromMeta) return String(fromMeta).replace(/\/$/, '');
  return 'http://localhost:4300';
}

let webAgentBaseUrl = readCtyWebAgentBaseUrl();

/** Root URL for the Web Agent runner (e.g. `http://localhost:4300`), no trailing slash. */
export function getWebAgentBaseUrl(): string {
  return webAgentBaseUrl;
}

export const setWebAgentConfig = ({ baseUrl: url }: { baseUrl: string }) => {
  const t = String(url ?? '').trim();
  webAgentBaseUrl = t.replace(/\/$/, '');
};

/** Host for the IDP releases API (customer-feed); set at runtime via `setIdpConfig` or from `CTY_IDP_API_URL` at build. */
function readCtyIdpBaseUrl(): string {
  const fromMeta = (import.meta as any).env?.CTY_IDP_API_URL;
  if (fromMeta) return String(fromMeta).replace(/\/$/, '');
  return '';
}

let idpBaseUrl = readCtyIdpBaseUrl();

/**
 * Root URL for the IDP API (e.g. `https://idp.circuitry.ai`), no trailing slash. Empty string when unconfigured.
 */
export function getIdpBaseUrl(): string {
  return idpBaseUrl;
}

export const setIdpConfig = ({ baseUrl: url }: { baseUrl: string }) => {
  const t = String(url ?? '').trim();
  idpBaseUrl = t.replace(/\/$/, '');
};

// Create our baseQuery instance
const baseQuery = fetchBaseQuery({
  baseUrl: baseUrl || (import.meta as any).env?.CTY_API_URL || new URL(window.location.href)?.origin,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState)?.auth?.token;
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  },
})

const baseQueryWithRetry = retry(baseQuery, { maxRetries: 0 })

const dynamicBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (args, api, extraOptions) => {
  const urlEnd = typeof args === 'string' ? args : args.url;
  const adjustedUrl = `${baseUrl}${urlEnd}`;
  const adjustedArgs =
    typeof args === 'string' ? adjustedUrl : { ...args, url: adjustedUrl };
  return baseQueryWithRetry(adjustedArgs, api, extraOptions);
}

/**
 * Create a base API to inject endpoints into elsewhere.
 * Components using this API should import from the injected site,
 * in order to get the appropriate types,
 * and to ensure that the file injecting the endpoints is loaded
 */
export const api = createApi({
  /**
   * `reducerPath` is optional and will not be required by most users.
   * This is useful if you have multiple API definitions,
   * e.g. where each has a different domain, with no interaction between endpoints.
   * Otherwise, a single API definition should be used in order to support tag invalidation,
   * among other features
   */
  reducerPath: 'ctyApi',
  /**
   * A bare bones base query would just be `baseQuery: fetchBaseQuery({ baseUrl: '/' })`
   */
  baseQuery: dynamicBaseQuery,
  /**
   * Tag types must be defined in the original API definition
   * for any tags that would be provided by injected endpoints
   */
  tagTypes: ['Advisor', 'ChatAdvisor', 'Dashboard', 'Conversations', 'Knowledge', 'Settings', 'Workflow', 'Credentials', 'Watchlist', 'NotificationConfigs', 'EmailSubscription'],
  /**
   * This api has endpoints injected in adjacent files,
   * which is why no endpoints are shown below.
   * If you want all endpoints defined in the same file, they could be included here instead
   */
  endpoints: () => ({}),
})

export const enhancedApi = api.enhanceEndpoints({
  endpoints: () => ({
    getPost: () => 'test',
  }),
})


export const handleErrorResponse = (response: any) => {
  // Check if there's a message in the body
  if (response?.data?.message) {
    return response.data;
  }
  // Handle error based on status code or presence of error details
  const statusCode = response?.originalStatus;
  let errorMessage = "An unexpected error occurred"; // Default message
  switch (statusCode) {
    case 504:
      errorMessage = "Gateway timed out, please try again.";
      break;
    case 500:
      errorMessage = "Internal server error, please contact support.";
      break;
    case 403:
      errorMessage = "Access forbidden, you may not have the necessary permissions.";
      break;
    case 404:
      errorMessage = "Resource not found, please check the request.";
      break;
    case 401:
      errorMessage = "Unauthorized, please log in again.";
      break;
    case 400:
      errorMessage = "Bad request, please check the input data.";
      break;
    // Add additional cases as needed
    default:
      errorMessage = response?.error || "An unexpected error occurred";
      break;
  }
  return {
    message: errorMessage,
    status: statusCode,
    reason: response?.headers?.["x-error-reason"] || response?.headers?.["reason"] || "No additional details",
  };
};
